import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import {
  Search, Eye, Edit, Trash2, RefreshCw, AlertCircle, FileX, Users, ArrowLeft, X,
  Phone, Mail, MapPin, Building, CheckCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { BASE_URL } from "../Apis/apiConfig";
import "../Style/Member.css";
import "../Style/FinancialTables.css";
import "../Style/Dashboard.css";
import Swal from "sweetalert2";

Modal.setAppElement("#root");

const HistoryMember = () => {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [members, setMembers] = useState([]);
  const [editFormData, setEditFormData] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const totalItems = members.length;
  const totalPages = Math.ceil(totalItems / perPage);

  const filteredMembers = useMemo(() => {
    return members.filter(
      (member) =>
        member.memberName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.unitNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        member.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [members, searchTerm]);

  const paginated = filteredMembers.slice((page - 1) * perPage, page * perPage);
  const displayedTotal = filteredMembers.length;

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const handlePageChange = (p) => p >= 1 && p <= totalPages && setPage(p);
  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setPage(1);
  };

  const handleFormChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };

  const handleSaveEdit = () => {
    Swal.fire("Success", "Member history updated successfully!", "success");
    setIsEditModalOpen(false);
  };

  const handleEditClick = (member) => {
    setEditFormData(member);
    setIsEditModalOpen(true);
  };

  const handleDeleteMember = async (memberId, index) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete this member history record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        const societyName = localStorage.getItem("society");

        await axios.delete(
          `${BASE_URL}/api/member/delete-member-history/${memberId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: { societyName },
          },
        );

        const updated = [...members];
        updated.splice(index, 1);
        setMembers(updated);

        Swal.fire(
          "Deleted!",
          "Member history record has been deleted.",
          "success",
        );
      } catch (err) {
        console.error("Error deleting member history:", err);
        Swal.fire("Error", "Failed to delete member history record.", "error");
      }
    }
  };

  const handleRefresh = () => {
    fetchMemberHistory();
  };

  const handleDeleteAll = async () => {
    const result = await Swal.fire({
      title: "Are you absolutely sure?",
      text: "This will delete ALL history records permanently! This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete all!",
      cancelButtonText: "Cancel",
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          const token = localStorage.getItem("token");
          const societyName = localStorage.getItem("society");

          await axios.delete(
            `${BASE_URL}/api/member/delete-all-member-history`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              params: { societyName },
            },
          );

          setMembers([]);
          return true;
        } catch (err) {
          console.error("Error deleting all history:", err);
          Swal.showValidationMessage("Failed to delete all records");
          return false;
        }
      },
    });

    if (result.isConfirmed && result.value) {
      Swal.fire(
        "Deleted!",
        "All history records have been deleted.",
        "success",
      );
    }
  };

  const fetchMemberHistory = async () => {
    const societyName = localStorage.getItem("society");
    const token = localStorage.getItem("token");

    if (!societyName || !token) {
      setError("Missing society name or authentication token");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/member/get-member-old-employee-histroy`,
        null,
        {
          params: { societyName },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      console.log("MEMBER HISTORY API Response:", response.data);
      if (response.data && Array.isArray(response.data)) {
        setMembers(response.data);
      } else {
        setMembers([]);
        setError("No data returned from server");
      }
    } catch (err) {
      console.error("Error fetching old employee history:", err);
      setError(err.response?.data?.message || "Failed to fetch member history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemberHistory();
  }, []);
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);
  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let p = 1; p <= totalPages; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }
    } else {
      let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
      let end = Math.min(totalPages, start + maxButtons - 1);

      if (end - start < maxButtons - 1) {
        start = Math.max(1, end - maxButtons + 1);
      }
      if (start > 1) {
        pages.push(
          <button
            key={1}
            className={`pagination-btn ${1 === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>,
        );
        if (start > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
      }
      for (let p = start; p <= end; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }
      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
        pages.push(
          <button
            key={totalPages}
            className={`pagination-btn ${totalPages === currentPage ? "active" : ""
              }`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>,
        );
      }
    }
    return pages;
  };

  return (
    <div className="app-container">
      <style>
        {`
        /* ========== RECEIPT PAGE OPTIMIZATIONS ========== */
        .app-container {
          height: 100vh;
          overflow: hidden !important;
        }

        .main-layout {
          height: calc(100vh - 64px);
          overflow: hidden !important;
        }

        .content-area {
          height: 100%;
          overflow-y: auto !important;
          overflow-x: hidden;
          padding-bottom: 20px;
        }

        .financial-container,
        .financial-card {
          height: auto !important;
          overflow: visible !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .financial-body {
          height: auto !important;
          overflow: visible !important;
          padding: 0 !important;
        }

        /* ========== COMPACT TABLE CONTROLS ========== */
        .controls-left,
        .controls-center,
        .controls-right {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .controls-center {
          flex: 1;
          justify-content: center;
        }

        .entries-selector label {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #64748b;
          font-weight: 500;
        }

        .entries-select {
          padding: 4px 8px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          background: white;
          font-size: 12px;
          color: #334155;
          cursor: pointer;
          height: 28px;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 4px;
          border: none;
        }

        .pagination-nav {
          padding: 4px 10px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          color: #334155;
          font-size: 11px;
          font-weight: 500;
          height: 26px;
          transition: all 0.2s;
        }

        .pagination-nav:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-nav:not(:disabled):hover {
          background: #f1f5f9;
        }

        .pagination-btn {
          min-width: 26px;
          height: 26px;
          padding: 4px 8px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          color: #334155;
          font-size: 11px;
          font-weight: 500;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .pagination-btn:hover {
          background: #f1f5f9;
        }

        .pagination-btn.active {
          background: #0d6efd;
          color: white;
          border-color: #0d6efd;
        }

        .pagination-ellipsis {
          padding: 0 4px;
          color: #94a3b8;
          font-size: 11px;
        }

        .entries-info {
          font-size: 11px;
          color: #64748b;
          white-space: nowrap;
        }

        /* ========== TABLE OPTIMIZATION ========== */
        .financial-table-container {
          overflow-x: auto;
          padding: 0;
          margin: 0;
        }

        .financial-table,
        .data-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: auto;
        }

        .financial-table th,
        .data-table th {
          padding: 6px 8px !important;
          text-align: center !important;
          vertical-align: middle;
          white-space: nowrap;
          font-weight: 600;
          font-size: 11px !important;
          background-color: #f8fafc;
          border-bottom: 2px solid #e2e8f0;
          border-right: 1px solid #e2e8f0;
          line-height: 1.2 !important;
          height: 32px !important;
        }

        .financial-table td,
        .data-table td {
          padding: 5px 8px !important;
          text-align: center !important;
          vertical-align: middle;
          font-size: 10px !important;
          border-bottom: 1px solid #e2e8f0;
          border-right: 1px solid #e2e8f0;
          line-height: 1.3 !important;
          height: 36px !important;
        }

        /* Checkbox Column */
        .financial-table th:nth-child(1),
        .financial-table td:nth-child(1),
        .data-table th:first-child,
        .data-table td:first-child {
          width: 35px !important;
          min-width: 35px !important;
          max-width: 35px !important;
          padding: 5px 2px !important;
        }

        /* Actions Column */
        .financial-table th:last-child,
        .financial-table td:last-child,
        .data-table th:last-child,
        .data-table td:last-child {
          width: 100px !important;
          min-width: 100px !important;
          padding: 4px 2px !important;
        }

        /* Action Buttons */
        .action-buttons {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 4px;
          padding: 0;
          margin: 0;
        }

        .action-icon-btn,
        .icon-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 3px;
          border-radius: 3px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
          width: 24px;
          height: 24px;
        }

        .action-icon-btn svg,
        .icon-btn svg {
          width: 14px;
          height: 14px;
        }

        .action-icon-btn:hover,
        .icon-btn:hover {
          background-color: #f1f5f9;
        }

        .icon-btn.edit {
          color: #0d6efd;
        }

        /* Checkbox */
        .financial-table input[type="checkbox"],
        .data-table input[type="checkbox"] {
          margin: 0;
          width: 13px;
          height: 13px;
          vertical-align: middle;
          cursor: pointer;
        }

        /* Row Hover */
        .financial-table tbody tr:hover td,
        .data-table tbody tr:hover td {
          background-color: #f8fafc;
        }

        .selected-row td {
          background-color: #e3f2fd !important;
        }

        /* Cell Content */
        .cell-content {
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }

        /* Status Badges */
        .status-badge {
          padding: 2px 6px !important;
          border-radius: 10px !important;
          font-size: 9px !important;
          font-weight: 600;
          display: inline-block;
          line-height: 1.2;
        }

        .status-paid {
          background-color: #d4edda;
          color: #155724;
        }

        .status-pending {
          background-color: #fff3cd;
          color: #856404;
        }

        .status-overdue {
          background-color: #f8d7da;
          color: #721c24;
        }

        /* Empty State */
        .financial-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          min-height: 300px;
        }

        .financial-state h3 {
          margin: 16px 0 8px 0;
          color: #334155;
          font-size: 18px;
          font-weight: 600;
        }

        .financial-state p {
          color: #64748b;
          font-size: 14px;
          margin: 0;
        }

        /* Loading Animation */
        .spin-animation {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .controls-left,
          .controls-center,
          .controls-right {
            width: 100%;
            justify-content: space-between;
          }
        }
        `}
      </style>
      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />
          <div className="financial-container">
            <div className="financial-card">
              <div className="flex px-4 bg-blue-400 rounded-tr-lg rounded-tl-lg text-white py-2">
                <button onClick={() => navigate(-1)}
                  className="back-button"
                  title="Go back"
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    padding: "2px 4px", borderRadius: "4px", display: "flex",
                    alignItems: "center", justifyContent: "center", color: "white",
                    transition: "background-color 0.2s", marginRight: "10px"
                  }}
                  onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    "rgba(255,255,255,0.2)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "transparent")
                  }
                >
                  <ArrowLeft size={16} />
                </button>
                <div className="controls-left">
                  <div className="entries-selector">
                    <label className="text-white">
                      Show
                      <select
                        value={perPage}
                        onChange={handlePerPageChange}
                        className="entries-select"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      entries
                    </label>
                  </div>
                </div>
                <div className="controls-center">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Search deleted members..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setPage(1);
                        }}
                        className="pl-8 pr-8 py-1 border border-gray-300 rounded text-sm text-gray-700"
                        style={{
                          width: "200px",
                          height: "28px",
                          fontSize: "12px",
                        }}
                      />
                      {searchTerm && (
                        <button
                          onClick={() => {
                            setSearchTerm("");
                            setPage(1);
                          }}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        >
                          <X
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                        </button>
                      )}
                    </div>
                    <button
                      className="rounded border-none flex bg-blue-300 text-white hover:bg-blue-700 p-2"
                      onClick={handleRefresh}
                      disabled={loading}
                      title="Refresh deleted members list" >
                      <RefreshCw
                        size={14}
                        className={loading ? "spin-animation" : ""}
                      />
                    </button>

                    <button
                      className="rounded border-none flex align-center bg-red-300 text-white hover:bg-red-500 p-2"
                      onClick={handleDeleteAll}
                      title="Delete all history records"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="controls-right">
                  {!loading && displayedTotal > 0 && (
                    <span className="entries-info text-white">
                      {(page - 1) * perPage + 1}-
                      {Math.min(page * perPage, displayedTotal)} of{" "}
                      {displayedTotal}
                    </span>
                  )}

                  <div className="flex gap-2 p-2">
                    <button className="pagination-nav" onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                      Prev
                    </button>
                    {renderPageNumbers()}
                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
              <div className="financial-body">
                {loading ? (
                  <div className="financial-state">
                    <RefreshCw size={48} className="spin-animation" />
                    <h3>Loading Deleted Members</h3>
                    <p>Please wait while we fetch the data...</p>
                  </div>
                ) : error ? (
                  <div className="financial-state error-state">
                    <AlertCircle size={48} />
                    <h3>Unable to Load Data</h3>
                    <p>{String(error)}</p>
                    <button
                      onClick={fetchMemberHistory}
                      style={{
                        marginTop: "20px", padding: "10px 20px", background: "#0d6efd",
                        color: "white", border: "none", borderRadius: "6px", cursor: "pointer",
                      }}
                    >
                      Try Again
                    </button>
                  </div>
                ) : members.length === 0 ? (
                  <div className="financial-state">
                    <div
                      style={{
                        background: "#f8fafc",
                        padding: "40px",
                        borderRadius: "16px",
                        textAlign: "center",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <FileX size={64} color="#94a3b8" />
                      <h3
                        style={{
                          margin: "20px 0 10px 0",
                          color: "#334155",
                          fontSize: "20px",
                          fontWeight: "600",
                        }}
                      >
                        No Deleted Members Found
                      </h3>
                      <p
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          margin: 0,
                          lineHeight: "1.5",
                        }}
                      >
                        {searchTerm
                          ? "No deleted members match your search criteria."
                          : "There are no deleted members recorded in the history."}
                      </p>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          style={{
                            marginTop: "20px",
                            padding: "8px 20px",
                            background: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            color: "#475569",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="financial-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>SR NO.</th>
                          <th>MEMBER NAME</th>
                          <th>UNIT NO</th>
                          <th>PHONE NUMBER</th>
                          <th>EMAIL</th>
                          <th>ADDRESS</th>
                          <th>DELETED DATE</th>
                          <th>ACTIONS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginated.map((row, idx) => {
                          const absoluteIdx = (page - 1) * perPage + idx;
                          return (
                            <tr key={row.id || idx}>
                              <td>{absoluteIdx + 1}</td>
                              <td>{row.memberName || "-"}</td>
                              <td>{row.unitNo || "-"}</td>
                              <td>{row.phoneNumber || "-"}</td>
                              <td>{row.email || "-"}</td>
                              <td>{row.address || "-"}</td>
                              <td>
                                {row.createdAt
                                  ? new Date(row.createdAt).toLocaleDateString(
                                    "en-GB",
                                  )
                                  : row.deletedDate || "-"}
                              </td>
                              <td>
                                <div className="action-buttons">
                                  <button
                                    className="icon-btn edit"
                                    title="View Details"
                                    onClick={() => {

                                      const deletedDate = row.createdAt
                                        ? new Date(row.createdAt)
                                        : row.deletedDate
                                          ? new Date(row.deletedDate)
                                          : null;
                                      const formattedDate = deletedDate
                                        ? deletedDate.toLocaleDateString(
                                          "en-GB",
                                          {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          },
                                        )
                                        : "Not recorded";

                                      let timeAgo = "";
                                      if (deletedDate) {
                                        const diff = Math.floor(
                                          (Date.now() - deletedDate) /
                                          (1000 * 60 * 60 * 24),
                                        );
                                        timeAgo =
                                          diff > 0
                                            ? `${diff} day${diff > 1 ? "s" : ""} ago`
                                            : "Today";
                                      }

                                      Swal.fire({
                                        title: ``,
                                        html: `
      <div style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;">
        <!-- Member Header -->
        <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 16px; padding: 12px; background: #f8f9fa; border-radius: 10px;">
          <div style="width: 44px; height: 44px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; font-weight: 600; font-size: 16px;">
            ${row.memberName ? row.memberName.charAt(0).toUpperCase() : "?"}
          </div>
          <div style="flex: 1;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
              <h4 style="margin: 0; color: #2d3436; font-size: 16px; font-weight: 600;">${row.memberName || "Unknown Member"}</h4>
              <span style="background: #ffeaa7; color: #d35400; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 600;">
                ${timeAgo}
              </span>
            </div>
            <p style="margin: 0; color: #636e72; font-size: 13px; text-align: left; ">Unit ${row.unitNo || "N/A"}</p>
          </div>
        </div>

        <!-- Details Grid -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px;">
          <!-- Phone -->
          <div style="background: #e8f4fd; padding: 10px; border-radius: 8px; border-left: 3px solid #3498db;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3498db" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
              </svg>
              <span style="font-size: 12px; color: #3498db; font-weight: 600;">PHONE</span>
            </div>
            <p style="margin: 0; color: #2d3436; font-size: 13px; font-weight: 500;">${row.phoneNumber || "—"}</p>
          </div>

          <!-- Email -->
          <div style="background: #f0f7ff; padding: 10px; border-radius: 8px; border-left: 3px solid #9b59b6;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9b59b6" stroke-width="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <span style="font-size: 12px; color: #9b59b6; font-weight: 600;">EMAIL</span>
            </div>
            <p style="margin: 0; color: #2d3436; font-size: 13px; font-weight: 500; word-break: break-all;">${row.email || "—"}</p>
          </div>
        </div>

        <!-- Deletion Date -->
        <div style="background: #fff5f5; padding: 12px; border-radius: 8px; margin-bottom: 12px; border: 1px solid #ffcccc;">
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" stroke-width="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span style="font-size: 12px; color: #e74c3c; font-weight: 600;">DELETED ON</span>
            </div>
            <span style="font-size: 13px; color: #2d3436; font-weight: 500;">${formattedDate}</span>
          </div>
        </div>

        <!-- Address -->
        <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; margin-bottom: 15px;">
          <div style="display: flex; align-items: flex-start; gap: 10px;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#27ae60" stroke-width="2" style="margin-top: 2px; flex-shrink: 0;">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <div style="flex: 1;">
              <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 6px;">
                <span style="font-size: 12px; color: #27ae60; font-weight: 600;">ADDRESS</span>
              </div>
              <p style="margin: 0; color: #2d3436; font-size: 13px; text-align: left; ">${row.address || "No address provided"}</p>
            </div>
          </div>
        </div>

        <!-- Footer Note -->
        <div style="text-align: center; padding: 10px; background: #f8f9fa; border-radius: 8px;">
          <p style="margin: 0; color: #7f8c8d; font-size: 11px; display: flex; align-items: center; justify-content: center; gap: 6px;">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7f8c8d" stroke-width="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            This record is preserved for reference only
          </p>
        </div>
      </div>
    `,
                                        width: 480,
                                        padding: "20px",
                                        showConfirmButton: true,
                                        confirmButtonText: "Close",
                                        confirmButtonColor: "#667eea",
                                        showCloseButton: true,
                                        customClass: {
                                          popup: "swal2-popup-compact",
                                          title: "swal2-title-compact",
                                        },
                                      });
                                    }}
                                  >
                                    <Eye size={14} />
                                  </button>
                                  <button
                                    className="icon-btn edit"
                                    title="Restore Member"
                                    onClick={() => {
                                      Swal.fire(
                                        "Coming Soon",
                                        "Restore functionality will be available soon.",
                                        "info",
                                      );
                                    }}
                                  >
                                    <RefreshCw size={14} />
                                  </button>
                                  <button
                                    className="icon-btn edit"
                                    title="Permanently Delete"
                                    onClick={() =>
                                      handleDeleteMember(
                                        row.id || row._id,
                                        absoluteIdx,
                                      )
                                    }
                                  >
                                    <Trash2 size={14} color="#dc3545" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Modal
            isOpen={isEditModalOpen}
            onRequestClose={() => setIsEditModalOpen(false)}
            style={{
              content: {
                top: "50%",
                left: "50%",
                right: "auto",
                bottom: "auto",
                marginRight: "-50%",
                transform: "translate(-50%, -50%)",
                width: "90%",
                maxWidth: "600px",
                maxHeight: "90vh",
                padding: "0",
                border: "none",
                borderRadius: "12px",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                overflow: "hidden",
              },
              overlay: {
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                backdropFilter: "blur(4px)",
                zIndex: 1000,
              },
            }}
          >
            <div
              className="edit-modal-content"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                minHeight: "100%",
              }}
            >
              <div
                className="edit-modal-header"
                style={{
                  padding: "24px 32px",
                  borderBottom: "1px solid rgba(255,255,255,0.1)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: "white",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <Users size={24} />
                  </div>
                  <div>
                    <h2
                      className="edit-modal-title"
                      style={{
                        margin: 0,
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "white",
                      }}
                    >
                      Edit Member Details
                    </h2>
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "14px",
                        opacity: 0.9,
                        color: "rgba(255,255,255,0.9)",
                      }}
                    >
                      Update deleted member information
                    </p>
                  </div>
                </div>
                <button
                  className="edit-modal-close"
                  onClick={() => setIsEditModalOpen(false)}
                  style={{
                    background: "rgba(255,255,255,0.1)", border: "none", cursor: "pointer",
                    padding: "10px", borderRadius: "10px", color: "white", display: "flex", alignItems: "center",
                    justifyContent: "center", transition: "all 0.3s", width: "44px", height: "44px",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(255,255,255,0.2)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "rgba(255,255,255,0.1)")
                  }
                >
                  <X size={20} />
                </button>
              </div>

              <div
                className="edit-modal-body"
                style={{
                  padding: "32px",
                  background: "white",
                  minHeight: "400px",
                  maxHeight: "60vh",
                  overflowY: "auto",
                }}
              >
                {editFormData && (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSaveEdit();
                    }}
                    className="edit-form"
                    style={{ width: "100%" }}
                  >
                    <div
                      className="form-grid"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "24px",
                        marginBottom: "32px",
                      }}
                    >
                      <div className="form-group">
                        <label
                          className="form-label"
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#334155",
                          }}
                        >
                          Member Name
                          <span style={{ color: "#ef4444", marginLeft: "4px" }}>
                            *
                          </span>
                        </label>
                        <div style={{ position: "relative" }}>
                          <div
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#94a3b8",
                            }}
                          >
                            <Users size={16} />
                          </div>
                          <input
                            type="text"
                            name="memberName"
                            value={editFormData.memberName || ""}
                            onChange={handleFormChange}
                            className="form-input"
                            required
                            style={{
                              width: "100%",
                              padding: "14px 14px 14px 40px",
                              border: "2px solid #e2e8f0",
                              borderRadius: "10px",
                              fontSize: "15px",
                              color: "#334155",
                              background: "#f8fafc",
                              transition: "all 0.3s",
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = "#667eea";
                              e.target.style.background = "white";
                              e.target.style.boxShadow =
                                "0 0 0 3px rgba(102, 126, 234, 0.1)";
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = "#e2e8f0";
                              e.target.style.background = "#f8fafc";
                              e.target.style.boxShadow = "none";
                            }}
                            placeholder="Enter member name"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label
                          className="form-label"
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#334155",
                          }}
                        >
                          Unit Number
                          <span style={{ color: "#ef4444", marginLeft: "4px" }}>
                            *
                          </span>
                        </label>
                        <div style={{ position: "relative" }}>
                          <div
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#94a3b8",
                            }}
                          >
                            <Building size={16} />
                          </div>
                          <input
                            type="text"
                            name="unitNo"
                            value={editFormData.unitNo || ""}
                            onChange={handleFormChange}
                            className="form-input"
                            required
                            style={{
                              width: "100%",
                              padding: "14px 14px 14px 40px",
                              border: "2px solid #e2e8f0",
                              borderRadius: "10px",
                              fontSize: "15px",
                              color: "#334155",
                              background: "#f8fafc",
                              transition: "all 0.3s",
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = "#667eea";
                              e.target.style.background = "white";
                              e.target.style.boxShadow =
                                "0 0 0 3px rgba(102, 126, 234, 0.1)";
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = "#e2e8f0";
                              e.target.style.background = "#f8fafc";
                              e.target.style.boxShadow = "none";
                            }}
                            placeholder="e.g., A-101, B-202"
                          />
                        </div>
                      </div>
                      <div className="form-group">
                        <label
                          className="form-label"
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#334155",
                          }}
                        >
                          Phone Number
                          <span style={{ color: "#ef4444", marginLeft: "4px" }}>
                            *
                          </span>
                        </label>
                        <div style={{ position: "relative" }}>
                          <div
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#94a3b8",
                            }}
                          >
                            <Phone size={16} />
                          </div>
                          <input
                            type="tel"
                            name="phoneNumber"
                            value={editFormData.phoneNumber || ""}
                            onChange={handleFormChange}
                            className="form-input"
                            required
                            pattern="[0-9]{10}"
                            style={{
                              width: "100%",
                              padding: "14px 14px 14px 40px",
                              border: "2px solid #e2e8f0",
                              borderRadius: "10px",
                              fontSize: "15px",
                              color: "#334155",
                              background: "#f8fafc",
                              transition: "all 0.3s",
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = "#667eea";
                              e.target.style.background = "white";
                              e.target.style.boxShadow =
                                "0 0 0 3px rgba(102, 126, 234, 0.1)";
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = "#e2e8f0";
                              e.target.style.background = "#f8fafc";
                              e.target.style.boxShadow = "none";
                            }}
                            placeholder="10-digit mobile number"
                          />
                        </div>
                      </div>

                      {/* Email */}
                      <div className="form-group">
                        <label
                          className="form-label"
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#334155",
                          }}
                        >
                          Email Address
                        </label>
                        <div style={{ position: "relative" }}>
                          <div
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#94a3b8",
                            }}
                          >
                            <Mail size={16} />
                          </div>
                          <input
                            type="email"
                            name="email"
                            value={editFormData.email || ""}
                            onChange={handleFormChange}
                            className="form-input"
                            style={{
                              width: "100%",
                              padding: "14px 14px 14px 40px",
                              border: "2px solid #e2e8f0",
                              borderRadius: "10px",
                              fontSize: "15px",
                              color: "#334155",
                              background: "#f8fafc",
                              transition: "all 0.3s",
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = "#667eea";
                              e.target.style.background = "white";
                              e.target.style.boxShadow =
                                "0 0 0 3px rgba(102, 126, 234, 0.1)";
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = "#e2e8f0";
                              e.target.style.background = "#f8fafc";
                              e.target.style.boxShadow = "none";
                            }}
                            placeholder="member@example.com"
                          />
                        </div>
                      </div>

                      {/* Address - Full Width */}
                      <div
                        className="form-group"
                        style={{ gridColumn: "span 2" }}
                      >
                        <label
                          className="form-label"
                          style={{
                            display: "block",
                            marginBottom: "8px",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#334155",
                          }}
                        >
                          Full Address
                        </label>
                        <div style={{ position: "relative" }}>
                          <div
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "16px",
                              color: "#94a3b8",
                            }}
                          >
                            <MapPin size={16} />
                          </div>
                          <textarea
                            name="address"
                            value={editFormData.address || ""}
                            onChange={handleFormChange}
                            className="form-input"
                            rows="3"
                            style={{
                              width: "100%",
                              padding: "14px 14px 14px 40px",
                              border: "2px solid #e2e8f0",
                              borderRadius: "10px",
                              fontSize: "15px",
                              color: "#334155",
                              background: "#f8fafc",
                              transition: "all 0.3s",
                              resize: "vertical",
                              minHeight: "100px",
                              fontFamily: "inherit",
                            }}
                            onFocus={(e) => {
                              e.target.style.borderColor = "#667eea";
                              e.target.style.background = "white";
                              e.target.style.boxShadow =
                                "0 0 0 3px rgba(102, 126, 234, 0.1)";
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = "#e2e8f0";
                              e.target.style.background = "#f8fafc";
                              e.target.style.boxShadow = "none";
                            }}
                            placeholder="Enter complete address..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Form Actions */}
                    <div
                      className="form-actions"
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: "16px",
                        paddingTop: "24px",
                        borderTop: "1px solid #e2e8f0",
                        marginTop: "8px",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setIsEditModalOpen(false)}
                        className="btn-cancel"
                        style={{
                          padding: "14px 28px",
                          border: "2px solid #e2e8f0",
                          borderRadius: "10px",
                          background: "white",
                          color: "#64748b",
                          fontSize: "15px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f8fafc";
                          e.currentTarget.style.borderColor = "#94a3b8";
                          e.currentTarget.style.color = "#475569";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "white";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                          e.currentTarget.style.color = "#64748b";
                        }}
                      >
                        <X size={16} />
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn-save"
                        style={{
                          padding: "14px 32px",
                          border: "none",
                          borderRadius: "10px",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          fontSize: "15px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 6px 20px rgba(102, 126, 234, 0.4)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 15px rgba(102, 126, 234, 0.3)";
                        }}
                      >
                        <CheckCircle size={16} />
                        Save Changes
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </Modal>
        </main>
      </div>
    </div>
  );
};

export default HistoryMember;
